import { useMutation, useQueryClient } from '@tanstack/react-query';

import { FulfillmentActionItemCode } from '@ekhodealer/ekho-common';

import type { MarkInspectedResponse } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { apiClient } from '@config/server/apiClient';
import { ordersEndpoints } from '@config/server/apiEndpoints';

import { updateProgressCache } from './progressCacheUtils';


/**
 * Input type for marking an order as inspected
 */
export interface MarkInspectedInput {
	orderId: string;
	inspection: {
		isComplete: boolean;
		dateInspected?: number;
		agent?: string;
		notes?: string;
	};
}

/**
 * API function to mark an order as inspected
 */
export const markInspected = async (
	servicesProviderId: string,
	{ orderId, inspection }: MarkInspectedInput,
): Promise<MarkInspectedResponse> => {
	const response = await apiClient.post<MarkInspectedResponse>(
		ordersEndpoints.markInspected(servicesProviderId, orderId),
		{ inspection },
	);
	return response.data;
};

/**
 * Hook for marking a purchased vehicle (order) as inspected
 * @param orderId - The purchased vehicle ID (orderId in frontend terms)
 */
export const useMarkInspected = (orderId: string | undefined) => {
	const servicesProviderId = useCurrentServicesProviderId();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (input: Omit<MarkInspectedInput, 'orderId'>) => {
			if (!orderId || !servicesProviderId) {
				throw new Error(
					'Unable to mark as inspected without an order ID or services provider ID.',
				);
			}
			return markInspected(servicesProviderId, {
				orderId,
				inspection: input.inspection,
			});
		},
		onSuccess: (data) => {
			if (!orderId || !servicesProviderId) return;
			updateProgressCache(queryClient, servicesProviderId, orderId, data.progress, FulfillmentActionItemCode.MARK_INSPECTED);
		},
	});
};
